import React, { useEffect, useMemo, useState, useRef } from 'react';
import { v4 as generateToken } from 'uuid';
import {
  AddressInput,
  addressInputItemBuilder,
  AddressInputProps,
  DropdownLayoutValueOption,
} from '@wix/design-system';
import { GetPlaceResponse, Place } from '@wix/ambassador-atlas-v2-place/types';
import {
  Prediction,
  PredictResponse,
} from '@wix/ambassador-atlas-v2-prediction/types';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import useDebouncedCallback from '../../../Hooks/useDebouncedCallback';
import { exploreAddressInputDataHooks } from './ExploreAddressInput.dataHooks';

interface ExploreAddressInputProps extends AddressInputProps {
  className?: string;
  onSelectPlace?: (location: GetPlaceResponse['place']) => void;
  onClear?: AddressInputProps['onClear'];
  onInputChange?: AddressInputProps['onChange'];
  statusProp?: AddressInputProps['status'];
  autoFocus?: boolean;
}

export const EXPLORE_ADDRESS_INPUT_DATA_HOOK = 'address-input';

const ExploreAddressInput = ({
  className,
  value,
  onInputChange,
  onSelectPlace,
  onClear,
  statusProp,
  border = 'standard',
  size = 'small',
  autoFocus = false,
  dataHook = exploreAddressInputDataHooks.addressInput,
}: ExploreAddressInputProps) => {
  const httpClient = useHttpClient();
  const { t } = useTranslation();
  const [predictions, setPredictions] = useState<Array<Prediction>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionToken = useRef(generateToken());

  const ref = useRef<any>(null);
  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
  }, [autoFocus, ref]);

  const status = isLoading ? 'loading' : statusProp;
  const options = useMemo(
    () =>
      predictions.map((prediction) =>
        addressInputItemBuilder({
          id: prediction.searchId ?? '',
          mainLabel: prediction?.textStructure?.mainText ?? '',
          secondaryLabel: prediction?.textStructure?.secondaryText ?? '',
          displayLabel: prediction.description ?? '',
          dataHook: exploreAddressInputDataHooks.locationPrediction,
        }),
      ),
    [predictions],
  );

  const clearPredictions = () => {
    setPredictions([]);
  };

  const updatePredictions = useDebouncedCallback(async () => {
    if (!value) {
      clearPredictions();
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await httpClient.post<PredictResponse>(
        '/api/location/predict',
        { input: value, sessionToken: sessionToken.current },
      );
      setPredictions(data?.predictions ?? []);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  }, [httpClient]);

  useEffect(() => {
    updatePredictions();
  }, []);

  const onAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange?.(event);
    updatePredictions();
  };

  const onAddressSelect = async (selection: DropdownLayoutValueOption) => {
    const {
      data: { place },
    } = await httpClient.post<{ place?: Place }>('/api/location/getPlace', {
      searchId: String(selection.id),
      sessionToken: sessionToken.current,
    });
    sessionToken.current = generateToken();
    onSelectPlace?.(place);
  };

  return (
    <AddressInput
      ref={ref}
      value={value}
      status={status}
      className={className}
      border={border}
      dataHook={dataHook}
      onChange={onAddressChange}
      options={options}
      placeholder={t('explorer-filter-placeholder-location')}
      onSelect={onAddressSelect}
      size={size}
      onClear={onClear}
      onFocus={(e) => e?.currentTarget?.click()}
    />
  );
};

export default ExploreAddressInput;
